/**
 * BOSS Resmi Tatiller sayfası.
 *
 * - Yıl seçici
 * - KKTC sabit tatilleri toplu ekle butonu
 * - Listede tatiller (tarih + isim + katsayı + düzenle/sil)
 * - "Yeni Tatil" butonu
 */

import { useCallback, useEffect, useState } from 'react'
import {
  CalendarDays, Plus, Trash2, Edit2, Sparkles, RefreshCw, AlertCircle,
} from 'lucide-react'
import {
  yilTatilleriGetir, tatilSil, kktcSabitTatilleriEkle,
  KATSAYI_ETIKETLERI,
  type ResmiTatil,
} from '@/lib/services/tatil-service'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useAuth } from '@/contexts/AuthContext'
import { TatilEkleModal } from './TatilEkleModal'

function tarihEtiketi(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('tr-TR', {
    day: 'numeric', month: 'long', weekday: 'long',
  })
}

function katsayiRengi(k: ResmiTatil['katsayi']): string {
  if (k === 'calismaz') return 'bg-gray-100 text-gray-700 border-gray-300'
  if (k === 1) return 'bg-blue-100 text-blue-800 border-blue-300'
  if (k === 1.5) return 'bg-amber-100 text-amber-800 border-amber-300'
  return 'bg-red-100 text-red-800 border-red-300'
}

export function ResmiTatillerPage() {
  const { firebaseUser } = useAuth()
  const buYil = new Date().getFullYear()
  const [yil, setYil] = useState(buYil)
  const [tatiller, setTatiller] = useState<ResmiTatil[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)
  const [ekleAcik, setEkleAcik] = useState(false)
  const [duzenlenen, setDuzenlenen] = useState<ResmiTatil | null>(null)
  const [silinecek, setSilinecek] = useState<ResmiTatil | null>(null)
  const [kktcEkleniyor, setKktcEkleniyor] = useState(false)
  const [basari, setBasari] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    setYukleniyor(true)
    try {
      const list = await yilTatilleriGetir(yil)
      setTatiller(list)
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Tatiller yüklenemedi')
    } finally {
      setYukleniyor(false)
    }
  }, [yil])

  useEffect(() => { yukle() }, [yukle])

  async function sil() {
    if (!silinecek) return
    try {
      await tatilSil(silinecek.id)
      setSilinecek(null)
      await yukle()
    } catch (err) {
      alert('Silinemedi: ' + (err as Error).message)
    }
  }

  async function kktcEkle() {
    if (!firebaseUser) return
    setKktcEkleniyor(true)
    setBasari(null)
    setHata(null)
    try {
      const r = await kktcSabitTatilleriEkle(yil, firebaseUser.uid)
      setBasari(`${r.eklenen} tatil eklendi${r.atlanan > 0 ? `, ${r.atlanan} zaten vardı` : ''}.`)
      await yukle()
      setTimeout(() => setBasari(null), 4000)
    } catch (err) {
      setHata((err as Error).message)
    } finally {
      setKktcEkleniyor(false)
    }
  }

  const yillar = [buYil - 1, buYil, buYil + 1, buYil + 2]

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Resmi Tatiller</h1>
          <p className="text-sm text-gray-600 mt-1">
            Tatil günleri ve katsayıları (maaş hesabında kullanılır)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={yil}
            onChange={(e) => setYil(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-semibold"
          >
            {yillar.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={yukle}
            disabled={yukleniyor}
            className="text-brand-600 hover:underline disabled:opacity-50 inline-flex items-center gap-1 text-sm"
          >
            <RefreshCw className={`w-4 h-4 ${yukleniyor ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">
          {hata}
        </div>
      )}

      {basari && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-3 text-sm">
          {basari}
        </div>
      )}

      {/* Aksiyon butonları */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <button
          onClick={() => { setDuzenlenen(null); setEkleAcik(true) }}
          className="btn-primary inline-flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Yeni Tatil Ekle
        </button>
        <button
          onClick={kktcEkle}
          disabled={kktcEkleniyor}
          className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-4 py-2.5 rounded-xl inline-flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <Sparkles className="w-5 h-5" />
          {kktcEkleniyor ? 'Ekleniyor...' : `${yil} KKTC Tatillerini Ekle`}
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-800">
          <strong>KKTC Tatilleri</strong>: 9 sabit tarihli tatil (Yılbaşı, 23 Nisan, 1 Mayıs, 19 Mayıs,
          20 Temmuz, 1 Ağustos, 30 Ağustos, 29 Ekim, 15 Kasım) varsayılan x1.5 katsayı ile eklenir.
          Dini bayramları (Ramazan/Kurban) elle eklemeniz gerekir — her yıl tarih değişir.
        </p>
      </div>

      {/* Liste */}
      {yukleniyor && tatiller.length === 0 ? (
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : tatiller.length === 0 ? (
        <div className="card text-center py-12">
          <CalendarDays className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">{yil} için tatil yok</p>
          <p className="text-sm text-gray-500 mt-1">
            "KKTC Tatillerini Ekle" ile toplu ekleyebilirsin.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {tatiller.map((t) => (
            <div key={t.id} className="card flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-gray-900 truncate">{t.isim}</h3>
                  {t.dini && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full font-semibold">
                      Dini
                    </span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-bold ${katsayiRengi(t.katsayi)}`}>
                    {KATSAYI_ETIKETLERI[String(t.katsayi)]}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-0.5">
                  {tarihEtiketi(t.tarih)}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={() => { setDuzenlenen(t); setEkleAcik(true) }}
                  className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-brand-600"
                  title="Düzenle"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setSilinecek(t)}
                  className="p-2 hover:bg-red-100 rounded-lg text-gray-600 hover:text-red-600"
                  title="Sil"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <TatilEkleModal
        acik={ekleAcik}
        duzenlenen={duzenlenen}
        onKapat={() => setEkleAcik(false)}
        onKaydedildi={() => {
          setEkleAcik(false)
          yukle()
        }}
      />

      <ConfirmDialog
        acik={!!silinecek}
        baslik="Tatili Sil"
        mesaj={silinecek ? `"${silinecek.isim}" (${tarihEtiketi(silinecek.tarih)}) silinsin mi?` : ''}
        onayMetni="Sil"
        tehlikeli
        onOnay={sil}
        onIptal={() => setSilinecek(null)}
      />
    </div>
  )
}
